import { AlertTriangle, CheckCircle2, Clock, FileText, Loader2, RotateCcw, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  cvProcessingFailureTranslationBase,
  getApplicationCvProcessingFailure,
  type CvProcessingFailureKind,
} from '@/lib/forkhire/cvProcessingFailure';

const FAILED_STATUSES = new Set(['cv_scoring_failed', 'rejected_unreadable']);
const PENDING_STATUSES = new Set(['cv_scoring_pending', 'duplicate_review_pending']);

type ReviewApplication = {
  status?: string | null;
  cv_file_url?: string | null;
  cv_parsed_json?: unknown;
  cv_scores?: {
    total_score?: number | null;
    explanation_text?: string | null;
    pre_shortlist_review?: {
      summary?: string | null;
      strengths?: unknown;
      gaps?: unknown;
    } | null;
    breakdown_json?: unknown;
  } | null;
};

function asStringArray(value: unknown, limit = 3): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        const row = item as Record<string, unknown>;
        return String(row.detail || row.reason || row.label || row.title || '').trim();
      }
      return '';
    })
    .filter(Boolean)
    .slice(0, limit);
}

function getRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function getReview(application: ReviewApplication | null | undefined) {
  const score = application?.cv_scores;
  const safeReview = score?.pre_shortlist_review;
  const breakdown = getRecord(score?.breakdown_json);
  const failureKind = getApplicationCvProcessingFailure(application);

  if (failureKind) {
    return {
      status: 'failed' as const,
      failureKind,
      score: null,
      summary: '',
      strengths: [],
      gaps: [],
    };
  }

  if (score?.total_score != null) {
    return {
      status: 'ready' as const,
      failureKind: null,
      score: score.total_score,
      summary: safeReview?.summary || score.explanation_text || 'AI CV review is ready for shortlist decisioning.',
      strengths: asStringArray(safeReview?.strengths ?? breakdown.strengths),
      gaps: asStringArray(safeReview?.gaps ?? breakdown.gaps ?? breakdown.weaknesses),
    };
  }

  if (FAILED_STATUSES.has(application?.status)) {
    return {
      status: 'failed' as const,
      failureKind: null,
      score: null,
      summary: application.status === 'rejected_unreadable'
        ? 'Original CV was uploaded, but parsing did not produce usable CV text.'
        : 'AI CV review failed. Retry is available from this application.',
      strengths: [],
      gaps: [],
    };
  }

  if (PENDING_STATUSES.has(application?.status) || application?.cv_file_url) {
    return {
      status: 'pending' as const,
      failureKind: null,
      score: null,
      summary: 'AI CV review is processing. Recruiter-safe results will appear here before shortlist.',
      strengths: [],
      gaps: [],
    };
  }

  return {
    status: 'empty' as const,
    failureKind: null,
    score: null,
    summary: 'No CV review is available for this application yet.',
    strengths: [],
    gaps: [],
  };
}

export function ApplicationAiReviewSummary({
  application,
  compact = false,
  onRetry,
  onReplace,
  retryPending = false,
  className,
}: {
  application: ReviewApplication | null | undefined;
  compact?: boolean;
  onRetry?: () => void;
  onReplace?: () => void;
  retryPending?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const review = getReview(application);
  const failureTranslationBase = review.failureKind
    ? cvProcessingFailureTranslationBase(review.failureKind)
    : null;
  const statusLabel = failureTranslationBase
    ? t(`${failureTranslationBase}.title`)
    : review.status === 'ready'
    ? 'AI review ready'
    : review.status === 'failed'
      ? 'AI review failed'
      : review.status === 'pending'
        ? 'AI review pending'
        : 'AI review unavailable';
  const Icon = review.status === 'ready'
    ? CheckCircle2
    : review.status === 'failed'
      ? AlertTriangle
      : review.status === 'pending'
        ? Clock
        : FileText;

  return (
    <div className={cn('rounded-lg border border-border bg-muted/20 p-3 space-y-2', compact && 'p-2.5', className)}>
      <div className="flex items-center gap-2">
        <Icon className={cn(
          'h-4 w-4 shrink-0',
          review.status === 'ready' && 'text-success',
          review.status === 'failed' && 'text-destructive',
          review.status === 'pending' && 'text-warning',
          review.status === 'empty' && 'text-muted-foreground',
        )} />
        <Badge variant={review.status === 'failed' ? 'destructive' : 'secondary'} className="text-[10px]">
          {statusLabel}
        </Badge>
        {review.score != null && (
          <span className="text-xs font-semibold text-foreground tabular-nums" dir="ltr">
            {review.score}/100
          </span>
        )}
      </div>
      <p className={cn(
        'text-xs text-muted-foreground leading-relaxed',
        compact ? 'line-clamp-2' : 'text-sm',
      )}>
        {failureTranslationBase ? t(`${failureTranslationBase}.description`) : review.summary}
      </p>
      {!compact && (review.strengths.length > 0 || review.gaps.length > 0) && (
        <div className="grid gap-2 sm:grid-cols-2">
          {review.strengths.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-success">Strengths</p>
              {review.strengths.map((item) => <p key={item} className="text-xs text-foreground">{item}</p>)}
            </div>
          )}
          {review.gaps.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-warning">Watchouts</p>
              {review.gaps.map((item) => <p key={item} className="text-xs text-foreground">{item}</p>)}
            </div>
          )}
        </div>
      )}
      {review.failureKind === 'TEMPORARY_RETRY' && onRetry && (
        <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" disabled={retryPending} onClick={onRetry}>
          {retryPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
          {t(`${failureTranslationBase}.action`)}
        </Button>
      )}
      {(review.failureKind === 'REPLACEMENT_REQUIRED_UNREADABLE' || review.failureKind === 'REPLACEMENT_REQUIRED_NOT_CV') && onReplace && (
        <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5" onClick={onReplace}>
          <Upload className="h-3.5 w-3.5" />
          {t(`${failureTranslationBase}.action`)}
        </Button>
      )}
    </div>
  );
}
